/**
 *
 * Badge
 *
 */

import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import _isNil from 'lodash/isNil';
import _get from 'lodash/get';
import Typography from '@material-ui/core/Typography';
import ClassNames from 'classnames';
import { capitalizeFirstLetter } from 'utils/stringUtils';
import UserIcon from 'components/Resources/images/Blue/People.svg';
import AppsIcon from 'components/Resources/images/Blue/Apps.svg';
import BooksIcon from 'components/Resources/images/Blue/Books.svg';
import PodcastIcon from 'components/Resources/images/Blue/Podcasts.svg';
import ArticleIcon from 'components/Resources/images/Blue/Articles.svg';
import VideosIcon from 'components/Resources/images/Blue/Videos.svg';
import ListsIcon from 'components/Resources/images/Blue/Lists.svg';
import AssessmentsIcon from 'components/Resources/images/Blue/Assessments.svg';
import OrganizationIcon from 'components/Resources/images/Blue/Organizations.svg';
import FaqIcon from 'components/Resources/images/Blue/FAQ.svg';
import FaqWhiteIcon from 'components/Resources/images/White/FAQ.svg';
import TopicsIcon from 'components/Resources/images/Blue/Topics.svg';
import NewsIcon from 'components/Resources/images/Blue/News.svg';
import ProgramIcon from 'components/Resources/images/Blue/Programs.svg';
import LinkIcon from 'components/Resources/images/Blue/Link.svg';
import BlogIcon from 'components/Resources/images/Blue/Blog.svg';
import ServiceIcon from 'components/Resources/images/Blue/services.svg';
import PracticeIcon from 'components/Resources/images/Blue/Practices.svg';
import SeriesWhiteIcon from 'components/Resources/images/White/Series.svg';
import SeriesIcon from 'components/Resources/images/Blue/Series.svg';
import AudioSeries from 'components/Resources/images/Blue/AudioSeries.svg';
import CoursesIcon from 'components/Resources/images/Blue/Courses.svg';
import WorksheetIcon from 'components/Resources/images/Blue/Worksheets.svg';
import useSiteCopySelector from 'components/useSiteCopySelector';
import SourceIcon from './new-tab.svg';
import CenterIcon from './center.svg';

const useStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    justifyContent: 'space-between',
    paddingRight: 20,
  },
  extraPaddingRight: {
    paddingRight: 28,
  },
  root: {
    left: 0,
    top: 0,
    margin: '12px 15px 2px 15px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    height: 27,
    color: theme.palette.primary.main,
    '& > svg': {
      background: theme.palette.primary.main,
      width: 15,
      marginRight: 10,
      color: theme.palette.primary.main,
    },
  },
  removeMargin: {
    margin: 0,
  },
  smallFont: {
    ...theme.typography.pxSmall,
    paddingTop: 2,
    '& > svg': {
      background: theme.palette.primary.main,
      width: 12,
      marginRight: 8,
      color: theme.palette.primary.main,
    },
  },
  icon: {
    margin: '0 1.5px',
    width: props => props.isPractice && 16,
  },
  center: {
    height: 12,
    width: 12,
  },
  label: {
    marginLeft: ({ hideIcon }) => !hideIcon && 6,
    paddingTop: 2,
    color: ({ color }) => color || theme.palette.text.secondary,
  },
  mobile: {
    left: 0,
    position: 'absolute',
  },
  peopleBadge: {
    position: 'absolute',
    width: 93,
  },
  peopleMobileBadge: {
    display: 'none',
  },
}));

export const Icons = {
  people: UserIcon,
  People: UserIcon,
  person: UserIcon,
  app: AppsIcon,
  Apps: AppsIcon,
  application: AppsIcon,
  book: BooksIcon,
  Books: BooksIcon,
  Podcasts: PodcastIcon,
  podcast: PodcastIcon,
  activity: ArticleIcon,
  article: ArticleIcon,
  Articles: ArticleIcon,
  video: VideosIcon,
  Videos: VideosIcon,
  organization: OrganizationIcon,
  Organizations: OrganizationIcon,
  faq: FaqIcon,
  FAQ: FaqIcon,
  faqWhite: FaqWhiteIcon,
  topic: TopicsIcon,
  topics: TopicsIcon,
  Topics: TopicsIcon,
  news: NewsIcon,
  News: NewsIcon,
  Link: LinkIcon,
  link: LinkIcon,
  program: ProgramIcon,
  'Online Programs': ProgramIcon,
  onlineProgram: ProgramIcon,
  Assessments: AssessmentsIcon,
  assessments: AssessmentsIcon,
  assessment: ArticleIcon,
  source: SourceIcon,
  landing: CenterIcon,
  blog: BlogIcon,
  Blog: BlogIcon,
  list: ListsIcon,
  Lists: ListsIcon,
  service: ServiceIcon,
  seriesWhite: SeriesWhiteIcon,
  series: SeriesIcon,
  practice: PracticeIcon,
  courses: CoursesIcon,
  course: CoursesIcon,
  worksheets: WorksheetIcon,
  audioSeries: AudioSeries,
};

// temporary
const LabelByType = {
  landing: 'Center',
  source: 'Explore',
  program: 'Online Program',
  onlineProgram: 'Online Program',
  activity: 'Articles',
  audioSeries: 'Audio Series',
};
function Badge(props) {
  const {
    type,
    isMobile,
    minutes,
    removeMargin,
    icon,
    color,
    data,
    siteCopyInOtherLang,
    customClasses,
    hideIcon,
  } = props;
  const [siteCopy] = useSiteCopySelector(['resource-item-page']);
  const classes = useStyles({
    color,
    hideIcon,
    isPractice: type === 'practice',
  });
  const hasMinute =
    ['podcast', 'video'].includes(type) && !_isNil(minutes) && minutes > 0;
  const imageType = type === 'blog' ? 'blog' : icon || type;
  const newIcons = ['source', 'landing'].includes(type);
  const isSourceItem = type === 'source';
  const title = isSourceItem
    ? _get(data, 'callToAction', 'Explore') || 'Explore'
    : LabelByType[type] || type;
  if (!title) return null;

  const formattedTitle =
    title !== 'faq' ? capitalizeFirstLetter(title) : title.toUpperCase();
  const finalTitle = _get(
    siteCopyInOtherLang || siteCopy,
    ['pageCopy', 'resourceCardTypeMapping', formattedTitle],
    formattedTitle,
  );
  return (
    <section
      className={ClassNames(
        classes.container,
        hasMinute && classes.extraPaddingRight,
        customClasses && customClasses.badgeRoot,
      )}
    >
      <div
        className={ClassNames(
          classes.root,
          customClasses && customClasses.badgeContainer,
          isMobile && classes.mobile,
          removeMargin && classes.removeMargin,
          // hasWideLabel && classes.wideLabel,
          // veryWideLabel && classes.veryWideLabel,
          type === 'people' && classes.peopleBadge,
          type === 'people' && isMobile && classes.peopleMobileBadge,
        )}
      >
        {!hideIcon && (
          <img
            src={Icons[imageType]}
            alt="card icon"
            className={ClassNames(classes.icon, { [classes.center]: newIcons })}
          />
        )}
        {!isMobile && (
          <Typography
            variant="overline"
            className={ClassNames(
              classes.label,
              removeMargin && classes.smallFont,
            )}
          >
            {finalTitle}
          </Typography>
        )}
      </div>
      {hasMinute && (
        <Typography
          className={ClassNames(
            classes.root,
            removeMargin && classes.removeMargin,
            removeMargin && classes.smallFont,
          )}
          variant="overline"
          align="center"
        >
          {minutes}
          {minutes < 2 ? ` min` : ` mins`}
        </Typography>
      )}
    </section>
  );
}

Badge.propTypes = {
  type: PropTypes.string,
  icon: PropTypes.string,
  isMobile: PropTypes.bool,
  minutes: PropTypes.any,
  removeMargin: PropTypes.bool,
  color: PropTypes.string,
  data: PropTypes.object,
  hideIcon: PropTypes.bool,
};

export default React.memo(Badge);
